import React, { useContext, useEffect, useState } from "react";
import SidePanel from "Components/DesignSystem/SidePanel/SidePanel";
import IconButton from "Components/DesignSystem/IconButton/IconButton";
import Button from "Components/DesignSystem/Button/Button";
import TextField from "Components/DesignSystem/TextField/TextField";
import Textarea from "Components/DesignSystem/Textarea/Textarea";
import ImagePlaceholder from "Components/DesignSystem/ImagePlaceholder/ImagePlaceholder";
import FieldTitle from "Components/DesignSystem/FieldTitle/FieldTitle";
import Typography from "Components/DesignSystem/Typography/Typography";
import { Cross } from "Components/DesignSystem/Iconography/Icons";
import AuthManager from "Common/AuthManager";
import Utils from "Common/Utils";
import { toast } from "Components/DesignSystem/Toast/useToast";
import ServiceWrapper from "Services/ServiceWrapper";
import AppSettings from "Settings/AppSettings";
import { ArsContext } from "Pages/GMS/ARS/Contexts/ArsContext";
import SelectCategoryType from "./SelectCategoryType";

type CreateCategoryFormData = {
    name: string;
    description: string;
    category_type_id: string;
    image_url: string;
    active: boolean;
}

type CreatedCategoryData = {
    id: string;
    name: string;
    cattype: string;
    categoryTypeName: string;
} | null;

type CreateCategoryModalProps = {
    isOpen: boolean;
    onClose: (createdCategory?: CreatedCategoryData) => Promise<void>;
    preSelectedCategoryTypeId?: string | null;
    setShowCreateCategoryTypeModal: React.Dispatch<React.SetStateAction<boolean>>
}

const CreateCategoryModal: React.FC<CreateCategoryModalProps> = ({
    isOpen,
    onClose,
    setShowCreateCategoryTypeModal,
    preSelectedCategoryTypeId = null
}) => {

    const { allCategoryTypes } = useContext(ArsContext);

    const [formData, setFormData] = useState<CreateCategoryFormData>({
        name: '',
        description: '',
        category_type_id: preSelectedCategoryTypeId || '',
        image_url: '',
        active: true
    });

    // Auto-select category type when preSelectedCategoryTypeId changes (e.g., after creating a new type)
    React.useEffect(() => {
        if (preSelectedCategoryTypeId) {
            setFormData((prev) => ({ ...prev, category_type_id: preSelectedCategoryTypeId }));
        }
    }, [preSelectedCategoryTypeId]);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    /* Empty means "render the DS ImagePlaceholder" (§ 191, the Category module's
       create-page pattern) — this used to hotlink a wikimedia No-Image SVG. */
    const [categoryImage, setCategoryImage] = useState<string>('');

    const validationOptions = {
        name: {
            required: true,
            minLength: 2,
            maxLength: 60,
            type: "alnum_with_spaces",
        },
        category_type_id: { required: true },
    };

    const isFormValid = (): boolean => {
        const validation = Utils.validate(formData, validationOptions);
        return Boolean(validation);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;

        setFormData((prev: CreateCategoryFormData) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Handle image URL change
        if (name === "image_url") {
            setCategoryImage(value || '');
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!isFormValid()) {
            toast.error('Please fill in all required fields correctly');
            return;
        }

        setIsLoading(true);

        try {
            const response = await ServiceWrapper.doPost(
                `${AppSettings.GMS_BACKEND_API_URL}category/create`,
                formData,
                null,
                AuthManager.getGmsToken()
            );

            if (!response.errorfound && response.data.done) {
                toast.success(response.data.message);
                // Find the category type name for the created category
                const categoryType = allCategoryTypes?.find((t: any) => t.id === formData.category_type_id);
                // Backend (Entities/category.py::create) returns {done, message, id}
                // at the top level of the JSON body → ServiceWrapper places it
                // in response.data, so the new UID is at response.data.id.
                // The old response.data.data?.id path was never populated,
                // which made createdCategory null and skipped the row auto-
                // select inside onCloseCategoryCreateModal.
                const newCategoryId = response.data?.id;
                const createdCategory: CreatedCategoryData = newCategoryId ? {
                    id: newCategoryId,
                    name: formData.name,
                    cattype: formData.category_type_id,
                    categoryTypeName: categoryType?.name || ''
                } : null;
                await onClose(createdCategory);
                setFormData({
                    name: '',
                    description: '',
                    category_type_id: preSelectedCategoryTypeId || '',
                    image_url: '',
                    active: true
                });
                setCategoryImage('');
            } else {
                toast.error(response.data.message || 'Failed to create category');
            }
        } catch (error) {
            toast.error('Error creating category');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateNewType = () => {
        setShowCreateCategoryTypeModal(true);
    };

    const handleCategoryTypeChange = (categoryTypeId: string) => {
        setFormData((prev: CreateCategoryFormData) => ({
            ...prev,
            category_type_id: categoryTypeId
        }));
    };

    if (!isOpen) return null;

    /*
        DS SidePanel (2026-09-05) in place of the hand-rolled fixed overlay.
        Scrim click and Escape both route through onClose (Escape is new); the
        legacy close-button testid/id stay on the headerActions IconButton, and
        the field testids stay on the real controls (DS TextField/Textarea put
        `data-testid` on the <input>/<textarea>). The name field keeps the
        native `required` — per okf/mapping/e2e.md § 7.1 that attribute is the
        ONLY thing actually blocking an empty submit (isFormValid() is a pinned
        always-true), so it must survive any reshaping of this form.
    */
    return (
        <SidePanel
            isOpen
            onClose={() => onClose()}
            width="lg"
            title="Create Category"
            tabs={[]}
            ariaLabel="Create Category"
            data-testid="create-category-modal"
            headerActions={
                <IconButton
                    size="sm"
                    aria-label="Close"
                    id="create-category-modal-close-btn"
                    data-testid="components-createcategorymodal-div-3"
                    onClick={() => onClose()}
                >
                    <Cross size={16} />
                </IconButton>
            }
        >
            <form onSubmit={handleSubmit} className="tw-flex tw-gap-4">
                {/* Form Section */}
                <div className="tw-flex-1 tw-space-y-6">
                    {/* Category Type Field */}
                    <div>
                        <FieldTitle label="Category Type" required className="tw-mb-2" />
                        <SelectCategoryType
                            value={formData.category_type_id}
                            onChange={handleCategoryTypeChange}
                            categoryTypes={allCategoryTypes}
                            onCreateNew={handleCreateNewType}
                            placeholder="Select Category Type"
                            isInvalid={!formData.category_type_id}
                        />
                    </div>

                    {/* Name Field */}
                    <div>
                        <TextField
                            label="Name"
                            required
                            // TODO(design-system): TextField maps `required` to
                            // aria-required ONLY (deliberately, to keep browser
                            // validation off) — but this form's only real
                            // empty-name block IS native validation
                            // (okf/mapping/e2e.md § 7.1), so the attribute is
                            // restored on the input through the forwarded ref.
                            ref={(el) => { if (el) el.required = true; }}
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={(_value, e) => e && handleInputChange(e)}
                            placeholder="Enter Category Name"
                            maxLength={60}
                            error={formData.name.length < 2}
                            data-testid="components-createcategorymodal-input-1"
                        />
                        <Typography variant="caption-metadata" as="div" className="tw-text-ink-tertiary tw-mt-1">
                            Name must be 2-60 characters long, contains letters, numbers and spaces and must not contain special characters.
                        </Typography>
                    </div>

                    {/* Image URL Field */}
                    <TextField
                        label="Image URL"
                        id="image_url"
                        name="image_url"
                        value={formData.image_url}
                        onChange={(_value, e) => e && handleInputChange(e)}
                        placeholder="Enter image url"
                        data-testid="components-createcategorymodal-input-2"
                    />

                    {/* Description Field */}
                    <Textarea
                        label="Description"
                        id="description"
                        name="description"
                        rows={3}
                        value={formData.description}
                        onChange={(_value, e) => e && handleInputChange(e)}
                        placeholder="Enter description"
                        data-testid="components-createcategorymodal-textarea-3"
                    />

                    {/* Footer Buttons */}
                    <div className="tw-flex tw-gap-2 tw-justify-start">
                        <Button
                            variant="secondary"
                            size="sm"
                            type="button"
                            data-testid="create-category-cancel-btn"
                            onClick={() => onClose()}
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            size="sm"
                            type="submit"
                            data-testid="create-category-submit-btn"
                            disabled={!isFormValid() || isLoading}
                            loading={isLoading}
                        >
                            {isLoading ? 'Creating...' : 'Save'}
                        </Button>
                    </div>
                </div>

                {/* Image Preview Section — § 191: a real URL renders the image
                    (broken URLs fall back via onError); empty renders the DS
                    ImagePlaceholder, exactly as the Category module's create page. */}
                <div className="tw-w-36 tw-flex-shrink-0 tw-mt-7">
                    {categoryImage ? (
                        <img
                            src={categoryImage}
                            className="tw-max-w-full tw-rounded tw-border tw-border-edge tw-p-1"
                            key={new Date().getTime()}
                            alt="Category preview"
                            onError={() => setCategoryImage('')}
                        />
                    ) : (
                        <ImagePlaceholder
                            width="100%"
                            height="150px"
                            data-testid="mapping-create-category-image-placeholder"
                        />
                    )}
                </div>
            </form>
        </SidePanel>
    );
};

export default CreateCategoryModal;
